'use client';

import { Button, cn, Skeleton } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { Expand } from '@shared/icons';
import ChevronRight from '@shared/icons/ChevronRight';
import type { IconProps } from '@shared/icons/types';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import type { EditableNavigationMenuItemProps, LinkNavigationMenuItemProps, NavigationMenuItemAction } from './types';

const isEditableNavigationMenuItem = (props: EditableNavigationMenuItemProps | LinkNavigationMenuItemProps): props is EditableNavigationMenuItemProps =>
  props.variant === 'editable';

function NavigationMenuItem(props: EditableNavigationMenuItemProps | LinkNavigationMenuItemProps) {
  const onVerify = isEditableNavigationMenuItem(props) ? props.onVerify : undefined;
  const propsWithoutOnVerify = isEditableNavigationMenuItem(props) ? (Object.fromEntries(Object.entries(props).filter(([key]) => key !== 'onVerify')) as typeof props) : props;
  const {
    href,
    target,
    rel,
    onClick,
    className,
    label,
    labelClassName,
    data = '',
    icon,
    chevron = true,
    expand = false,
    hierarchy = 'low',
    buttonText,
    buttonVariant = 'secondary',
    'data-testid': dataTestId,
    displayedValueTestId,
    dataInterval,
    notificationBubbleText,
    verified = true,
    namespace,
    verifyUrl,
    variant = 'link',
    statusText,
    statusIndicatorColor,
    statusAction,
    description,
    'data-track-name': dataTrackName,
    'data-track-privacy': dataTrackPrivacy,
    'data-track-container': dataTrackContainer,
    'data-testid-value': dataTestIdValue,
    'data-testid-status': dataTestIdStatus,
    isLoading,
    buttonTrackName = 'Edit',
    reversedLabelData = false,
    ...rest
  } = propsWithoutOnVerify as EditableNavigationMenuItemProps | LinkNavigationMenuItemProps;

  const wrapperClasses = cn('flex flex-col w-full py-5 px-1 paragraph-strong text-foreground text-wrap truncate', variant === 'link' && 'hover:bg-container', className);
  const t = useTranslations(namespace);
  const isAustralianMarket = getSiteConfig().REGISTRATION.country === 'AU';
  const subtleTextClass = isAustralianMarket ? 'paragraph-s' : 'paragraph-s-strong';

  const showVerificationRequired = !verified;
  const Icon = icon as React.ComponentType<IconProps>;

  const Chevron = ChevronRight as React.ComponentType<IconProps>;

  const WrapperElement = (variant === 'link' && href ? Link : 'div') as React.ElementType;

  const wrapperTestId = dataTestId ? `${dataTestId}-wrapper` : undefined;
  const buttonTestId = isEditableNavigationMenuItem(props) ? props['button-data-testid'] : undefined;
  const verifyButtonTestId = dataTestId ? `${dataTestId}-verify-button` : undefined;

  return (
    <WrapperElement
      {...(variant === 'link' && href ? { href: href as string } : {})}
      {...(variant === 'link' && target ? { target: target as string } : {})}
      {...(variant === 'link' && rel ? { rel: rel as string } : {})}
      {...(variant === 'link' && !href && onClick ? { onClick: onClick as () => void } : {})}
      className={cn('inline-block w-full', variant === 'link' && !href && onClick && 'cursor-pointer')}
      data-track-container={dataTrackContainer || undefined}
      data-track-name={dataTrackName || undefined}
      data-testid={wrapperTestId}
    >
      <div className={wrapperClasses} data-testid={dataTestId} {...rest}>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-1 w-full">
            <div className="flex flex-col items-start justify-center">
              <div className={cn(reversedLabelData && 'flex flex-col-reverse')}>
                <div className="flex items-center gap-4">
                  {icon && <Icon className="w-6 h-6" />}
                  {label && (
                    <span className={cn(labelClassName ?? (data ? 'paragraph-s' : 'paragraph'))} data-testid="navigation-menu-item-label">
                      {label}
                    </span>
                  )}
                </div>
                {data &&
                  (isLoading ? (
                    <div className="mt-1">
                      <Skeleton className="h-5 w-24" data-testid="data-skeleton" />
                    </div>
                  ) : (
                    <span
                      className={cn(hierarchy === 'low' ? 'paragraph-strong' : 'heading-4')}
                      data-track-privacy={dataTrackPrivacy || undefined}
                      data-interval={dataInterval || undefined}
                      data-testid={displayedValueTestId ?? dataTestIdValue ?? 'navigation-menu-item-data'}
                    >
                      {data}
                    </span>
                  ))}
              </div>
            </div>
            <div className="flex items-center gap-4">
              {notificationBubbleText && (
                <div
                  className="flex items-center justify-center paragraph-callout text-white bg-notification-red rounded-full p-0.5 h-4 w-4 border border-border-base box-content"
                  data-track-name="Notification"
                  data-testid="notification-bubble"
                >
                  <span>{notificationBubbleText}</span>
                </div>
              )}
              {isEditableNavigationMenuItem(props) &&
                buttonText &&
                !props.actions &&
                (isLoading ? (
                  <Skeleton className="btn-label-s py-2 px-4" data-testid="button-skeleton" />
                ) : (
                  <Button variant={buttonVariant} size="s" data-track-name={buttonTrackName} {...(buttonTestId ? { 'data-testid': buttonTestId } : {})} onClick={onClick}>
                    {buttonText}
                  </Button>
                ))}
              {chevron && <Chevron size="small" data-track-name="View" />}
              {expand && <Expand size="small" data-track-name="View" />}
            </div>
          </div>
          {description && <p className={cn(subtleTextClass, 'text-foreground-subtle')}>{description}</p>}
          {showVerificationRequired && (onVerify || verifyUrl) && (
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-warning-foreground h-2 w-2" /> <span className={cn(subtleTextClass, 'text-foreground-subtle')}>{t('not_verified')}</span>{' '}
              {onVerify ? (
                <button
                  type="button"
                  onClick={onVerify}
                  className="link-label-s text-foreground-subtle underline decoration-solid decoration-[12%] underline-offset-[20%]"
                  data-track-name="Verify"
                  data-testid={verifyButtonTestId}
                >
                  {t('verify_now')}
                </button>
              ) : (
                <Link
                  href={verifyUrl ?? ''}
                  className="link-label-s text-foreground-subtle underline decoration-solid decoration-[12%] underline-offset-[20%]"
                  data-track-name="Verify"
                  data-testid={verifyButtonTestId}
                >
                  {t('verify_now')}
                </Link>
              )}
            </div>
          )}
          {statusText && (
            <div className="flex items-center gap-2">
              <span className="rounded-full h-2 w-2" style={{ backgroundColor: statusIndicatorColor || '#606a86' }} />
              <span className={cn(subtleTextClass, 'text-foreground-subtle')} data-testid={dataTestIdStatus}>
                {statusText}
              </span>
              {statusAction && (
                <button
                  type="button"
                  onClick={statusAction.onClick}
                  className="link-label-s text-foreground underline decoration-solid decoration-[12%] underline-offset-[20%]"
                  data-track-name={statusAction['data-track-name']}
                  data-testid={statusAction['data-testid']}
                >
                  {statusAction.label}
                </button>
              )}
            </div>
          )}
        </div>
        {isEditableNavigationMenuItem(props) && props.actions && (
          <div className="bg-container mt-2 -mx-1 px-5 py-3">
            <div className="flex items-center justify-end gap-2">
              {props.actions.map((action: NavigationMenuItemAction, index: number) => (
                <Button key={index} variant={action.variant} size="s" onClick={action.onClick} data-testid={action['data-testid']} data-track-name={action['data-track-name']}>
                  {action.label}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </WrapperElement>
  );
}

export { NavigationMenuItem };
